import React, { useContext, useState, useEffect } from 'react';
import { Flex } from 'deprecated-enkel';
import { CreditCard, Search } from '@styled-icons/boxicons-regular';
import { Bill } from '@styled-icons/remix-fill';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ClipLoader } from 'react-spinners';

import { UserContext } from 'constants/user-context';
import { requiresStripePaymentMethod } from 'constants/organization';

import { PaymentCards } from 'pages/Settings/SettingsPage/SettingsPayment/PaymentCards';
import {
    getStripeApiKey,
    setInvoiceInitialValues,
    InvoiceValues,
} from 'pages/Settings/SettingsPage/SettingsPayment/utils';

import { Loading } from 'components/Loading';
import { BillingInfoUnavailable } from 'components/BillingInfoUnavailable';
import { CompanySearchSelect, SelectedCompany } from 'components/CompanySearchSelect';

import { InlineError } from 'ui/InlineError';
import { TotemButton } from 'ui/Button';

import { objectChangeCheck } from 'utils';
import { isValidVatFormat } from 'utils/vat';

import { TotemInput, TotemLabel } from 'styles';
import {
    SettingsTitleContainerWrapper,
    SettingsTitleContainer,
    SettingsTitle,
    SettingsSubtitle,
    SettingsPageStyle,
    SettingsCard,
    SettingsForm,
    SettingsLeftHalf,
    SettingsRightHalf,
} from 'styles/pages/settings.styles';

import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';
import { useOrganizationBillingInfo } from 'data/hooks/useOrganizationBillingInfo';
import { useSetStripeDefaultPaymentMethodMutation, useUpdateOrganizationBillingInfoMutation } from 'data/__generated__';

// Champs obligatoires pour émettre une facture (les autres — PO, TVA, SIRET — sont facultatifs).
const REQUIRED_FIELDS: Array<keyof InvoiceValues> = [
    'invoiceEmail',
    'invoiceName',
    'invoiceAddress',
    'invoiceCity',
    'invoiceZipcode',
    'invoiceCountry',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SettingsPagePayment = () => {
    const { organization, user } = useContext(UserContext);
    // Chargées à part de l'organisation : Odoo ne doit ni ralentir ni casser le reste de la page.
    const { billingInfo, isBillingInfoLoading, billingInfoError, refetchBillingInfo } = useOrganizationBillingInfo();
    const [stripePromise] = useState(() => loadStripe(getStripeApiKey(organization)));
    const [setStripeDefaultPaymentMethod] = useSetStripeDefaultPaymentMethodMutation();
    const [updateOrganizationBillingInfo, { loading: isLoading }] = useUpdateOrganizationBillingInfoMutation();

    const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
    const [selectedCardModified, setSelectedCardModified] = useState<boolean>(false);
    const [popupWindowOpen, setPopupWindowOpen] = useState<boolean>(false);

    // Source de vérité UNIQUE du formulaire de facturation. (Auparavant partagée entre react-hook-form
    // et un state local via un `onChange` qui écrasait celui de RHF → double état, d'où un bouton
    // « Mettre à jour » capricieux. On repart d'un seul state contrôlé.)
    const [invoiceValues, setInvoiceValues] = useState<InvoiceValues>(setInvoiceInitialValues(billingInfo));

    const hasCards = !!organization?.cards?.length;
    const isStripeCustomer = Boolean(organization?.stripeId);
    // Odoo encaisse, ou personne : aucun moyen de paiement Stripe à ajouter ni à gérer ici.
    const needsPaymentMethod = requiresStripePaymentMethod(organization?.paymentType);
    const originalInvoiceValues = setInvoiceInitialValues(billingInfo);

    let originalCardIndex: number | null = null;
    if (organization && organization.cards) {
        originalCardIndex = organization.cards.findIndex((card) => card.id === billingInfo?.defaultPaymentMethod);
    }

    // Resynchronise le formulaire quand les infos de facturation changent (ex. après une sauvegarde ou la
    // fin du chargement) : remet les valeurs de référence et neutralise l'état « modifié ».
    useEffect(() => {
        setInvoiceValues(setInvoiceInitialValues(billingInfo));
    }, [billingInfo]);

    useEffect(() => {
        if (hasCards && organization.cards) {
            const nextIndex = organization.cards.findIndex((card) => card.id === billingInfo?.defaultPaymentMethod);
            setSelectedCardIndex(nextIndex);
        }
    }, [billingInfo?.defaultPaymentMethod, organization?.cards, hasCards]);

    function setField(key: keyof InvoiceValues) {
        return (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            setInvoiceValues((v) => ({ ...v, [key]: value }));
        };
    }

    function handleSetStripeDefaultPaymentMethod(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        if (!user?._id) return;
        if (organization?.cards && selectedCardIndex !== null && selectedCardIndex >= 0) {
            setStripeDefaultPaymentMethod({
                variables: {
                    organizationId: organization?._id,
                    userId: user?._id,
                    paymentMethodId: organization?.cards[selectedCardIndex].id,
                },
            });
            setSelectedCardModified(false);
        }
    }

    // Sélection d'une entreprise : pré-remplit tout ce que Sirene fournit (raison sociale, TVA, SIRET et
    // adresse dérivée). On n'écrase jamais une saisie existante par une valeur vide.
    function handleSelectCompany(company: SelectedCompany) {
        setInvoiceValues((v) => ({
            ...v,
            invoiceName: company.legalName,
            invoiceVAT: company.vat ?? v.invoiceVAT,
            invoiceSiret: company.siret,
            invoiceAddress: company.address || v.invoiceAddress,
            invoiceZipcode: company.zipcode || v.invoiceZipcode,
            invoiceCity: company.city || v.invoiceCity,
        }));
    }

    async function handleUpdateInvoiceValues(e: React.FormEvent) {
        e.preventDefault();
        if (!organization?._id || !canSubmit) return;
        const { invoiceEmail, ...otherInvoiceValues } = invoiceValues;
        track(analyticsEvents.VALIDATE_BILLING_INFO, {
            [analyticsPropertiesConstants.COMMON.IS_ONBOARDING]: false,
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.SETTINGS,
            [analyticsPropertiesConstants.ORDER.BILLING.HAS_PO_NUMBER]: !!invoiceValues?.invoicePO,
            [analyticsPropertiesConstants.ORDER.BILLING.HAS_TVA_NUMBER]: !!invoiceValues?.invoiceVAT,
        });
        await updateOrganizationBillingInfo({
            variables: {
                organizationId: organization._id,
                organizationBillingInfo: { ...otherInvoiceValues, invoiceEmail: invoiceEmail.trim() },
            },
        });
    }

    if (!invoiceValues) {
        return <Loading relative />;
    }

    // --- Validation dérivée du state unique ---
    const isEmailValid = EMAIL_REGEX.test(invoiceValues.invoiceEmail.trim());
    const isVatValid = isValidVatFormat(invoiceValues.invoiceVAT);
    const isNameValid = invoiceValues.invoiceName.trim().length >= 3;
    const allRequiredFilled = REQUIRED_FIELDS.every((key) => !!invoiceValues[key]?.trim());
    const hasChanged = objectChangeCheck(originalInvoiceValues, invoiceValues);
    const hasFormatErrors =
        (!!invoiceValues.invoiceEmail && !isEmailValid) || !isVatValid || (!!invoiceValues.invoiceName && !isNameValid);

    const canSubmit = allRequiredFilled && isEmailValid && isNameValid && isVatValid && hasChanged;
    // Message explicite quand le bouton reste grisé (au lieu d'un bouton grisé « mystérieux »).
    const disabledReason = !hasChanged
        ? null
        : !allRequiredFilled
          ? 'Complétez les champs obligatoires (*).'
          : !isEmailValid
            ? "L'email de réception n'est pas valide."
            : !isNameValid
              ? 'La raison sociale doit contenir au moins 3 caractères.'
              : !isVatValid
                ? 'Le format du numéro de TVA est invalide.'
                : null;

    return (
        <SettingsPageStyle>
            {/* Section dédiée : identification automatique de l'entreprise (Sirene). */}
            <SettingsCard>
                <SettingsTitleContainerWrapper>
                    <SettingsTitleContainer>
                        <Search />
                        <SettingsTitle>Identifier mon entreprise</SettingsTitle>
                    </SettingsTitleContainer>
                </SettingsTitleContainerWrapper>
                <SettingsSubtitle>
                    Recherchez votre société par nom, SIRET ou n° de TVA pour pré-remplir automatiquement la raison
                    sociale, le numéro de TVA, le SIRET et l'adresse.
                </SettingsSubtitle>
                <TotemLabel>
                    <CompanySearchSelect onSelect={handleSelectCompany} />
                </TotemLabel>
            </SettingsCard>

            {/* Section : informations de facturation. */}
            <SettingsCard>
                <SettingsForm onSubmit={handleUpdateInvoiceValues}>
                    <SettingsTitleContainerWrapper>
                        <SettingsTitleContainer>
                            <Bill />
                            <SettingsTitle>Informations de facturation</SettingsTitle>
                        </SettingsTitleContainer>
                        {!billingInfoError && !isBillingInfoLoading && (
                            <TotemButton
                                $alignSelf="flex-end"
                                $fontSize="1em"
                                type="submit"
                                disabled={isLoading || !canSubmit}
                                $isLoading={isLoading}
                            >
                                {isLoading ? <ClipLoader color="white" size="16px" /> : 'Mettre à jour'}
                            </TotemButton>
                        )}
                    </SettingsTitleContainerWrapper>
                    {billingInfoError ? (
                        <BillingInfoUnavailable onRetry={refetchBillingInfo} />
                    ) : isBillingInfoLoading ? (
                        <Loading relative />
                    ) : (
                        <>
                            <Flex $flow="row" $wrap="wrap">
                                <SettingsLeftHalf>
                                    <TotemLabel>
                                        Email de réception des factures*{' '}
                                        <InlineError
                                            inline
                                            display={!!invoiceValues.invoiceEmail && !isEmailValid}
                                            message="Votre mail n'est pas valide"
                                        />
                                        <TotemInput
                                            placeholder="Entrez l'email de réception des factures"
                                            type="email"
                                            value={invoiceValues.invoiceEmail}
                                            onChange={setField('invoiceEmail')}
                                            data-test="payment-email"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        Raison sociale*{' '}
                                        <InlineError
                                            inline
                                            display={!!invoiceValues.invoiceName && !isNameValid}
                                            message="Au moins 3 caractères"
                                        />
                                        <TotemInput
                                            placeholder="Entrez le nom d'entreprise pour la facture"
                                            value={invoiceValues.invoiceName}
                                            onChange={setField('invoiceName')}
                                            data-test="payment-name"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        Numéro de PO
                                        <TotemInput
                                            placeholder="Entrez le numéro de PO de l'entreprise"
                                            value={invoiceValues.invoicePO}
                                            onChange={setField('invoicePO')}
                                            data-test="payment-PO-number"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        Numéro de TVA
                                        <TotemInput
                                            placeholder="Entrez le numéro de TVA de l'entreprise"
                                            value={invoiceValues.invoiceVAT}
                                            onChange={setField('invoiceVAT')}
                                            data-test="payment-VAT-number"
                                        />
                                        <InlineError
                                            inline
                                            display={!isVatValid}
                                            message="Le format du numéro est invalide"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        SIRET
                                        <TotemInput
                                            placeholder="Entrez le SIRET de l'entreprise"
                                            value={invoiceValues.invoiceSiret}
                                            onChange={setField('invoiceSiret')}
                                            data-test="payment-siret"
                                        />
                                    </TotemLabel>
                                </SettingsLeftHalf>
                                <SettingsRightHalf>
                                    <TotemLabel>
                                        Adresse*
                                        <TotemInput
                                            placeholder="Adresse"
                                            value={invoiceValues.invoiceAddress}
                                            onChange={setField('invoiceAddress')}
                                            data-test="payment-address"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        Ville*
                                        <TotemInput
                                            placeholder="Entrez la ville"
                                            value={invoiceValues.invoiceCity}
                                            onChange={setField('invoiceCity')}
                                            data-test="payment-city"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        Code postal*
                                        <TotemInput
                                            placeholder="Entrez le code postal"
                                            value={invoiceValues.invoiceZipcode}
                                            onChange={setField('invoiceZipcode')}
                                            data-test="payment-zipcode"
                                        />
                                    </TotemLabel>
                                    <TotemLabel>
                                        Pays*
                                        <TotemInput
                                            placeholder="Entrez le pays"
                                            value={invoiceValues.invoiceCountry}
                                            onChange={setField('invoiceCountry')}
                                            data-test="payment-country"
                                        />
                                    </TotemLabel>
                                </SettingsRightHalf>
                            </Flex>
                            <InlineError inline display={true} message={disabledReason ?? ''} />
                        </>
                    )}
                </SettingsForm>
            </SettingsCard>

            <SettingsCard>
                <SettingsTitleContainerWrapper>
                    <SettingsTitleContainer>
                        <CreditCard />
                        <SettingsTitle>Mes modes de paiement</SettingsTitle>
                    </SettingsTitleContainer>
                    <Flex>
                        {needsPaymentMethod && hasCards && isStripeCustomer && (
                            <TotemButton
                                $margins={[0, 2]}
                                onClick={handleSetStripeDefaultPaymentMethod}
                                $fontSize="1em"
                                disabled={!selectedCardModified}
                            >
                                Changer le mode de paiement par défaut
                            </TotemButton>
                        )}
                        {needsPaymentMethod && allRequiredFilled && !hasFormatErrors && (
                            <TotemButton
                                type="button"
                                $fontSize="1em"
                                onClick={() => {
                                    track(analyticsEvents.START_ADDING_PAYMENT_METHOD, {
                                        [analyticsPropertiesConstants.COMMON.IS_ONBOARDING]: false,
                                    });
                                    setPopupWindowOpen(true);
                                }}
                                data-test="payment-methods-popup-button"
                            >
                                Ajouter un mode de paiement
                            </TotemButton>
                        )}

                        {/* Sans le `!hasCards` d'origine : une organisation qui a déjà une carte mais des
                            infos de facturation incomplètes n'avait ni bouton d'ajout ni explication —
                            une section vide et muette. */}
                        {needsPaymentMethod && (!allRequiredFilled || hasFormatErrors) && !billingInfoError && (
                            <span>
                                Merci de compléter toutes les informations nécessaires ci-dessus pour ajouter un mode de
                                paiement
                            </span>
                        )}
                    </Flex>
                </SettingsTitleContainerWrapper>
                <Elements stripe={stripePromise}>
                    <PaymentCards
                        organization={organization}
                        billingInfo={billingInfo}
                        user={user}
                        hasCards={hasCards}
                        selectedCardIndex={selectedCardIndex}
                        originalCardIndex={originalCardIndex}
                        setSelectedCardIndex={setSelectedCardIndex}
                        setStripeDefaultPaymentMethod={setStripeDefaultPaymentMethod}
                        setSelectedCardModified={setSelectedCardModified}
                        popupWindowOpen={popupWindowOpen}
                        setPopupWindowOpen={setPopupWindowOpen}
                        invoiceValues={invoiceValues}
                    />
                </Elements>
            </SettingsCard>
        </SettingsPageStyle>
    );
};
